import { X, HelpCircle, Newspaper, Sparkles } from "lucide-react";
import { useState } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { useHelpStore } from "@/lib/store/help.store";
import { HelpArticleList } from "./help-article-list.component";
import { HelpArticleView } from "./help-article-view.component";
import { ReleaseNotesTab } from "./release-notes-tab.component";
import { AiChatTab } from "./ai-chat-tab.component";
import { useReleaseNotes } from "../release-notes/useReleaseNotes";

import type { HelpTab } from "@/lib/store/help.store";

interface HelpPanelProps {
	app: "dentlab" | "dentconnect";
	currentRoute?: string;
}

export const HelpPanel: React.FC<HelpPanelProps> = ({
	app,
	currentRoute = "/",
}) => {
	const closeHelp = useHelpStore((state) => state.closeHelp);
	const activeArticleSlug = useHelpStore(
		(state) => state.activeArticleSlug
	);
	const activeTab = useHelpStore((state) => state.activeTab);
	const setActiveTab = useHelpStore((state) => state.setActiveTab);
	const openArticle = useHelpStore((state) => state.openArticle);
	const [viewingSlug, setViewingSlug] = useState<string | null>(
		activeArticleSlug
	);

	const {
		releases,
		unreadCount,
		loading,
		hasMore,
		readIds,
		markAsRead,
		markAllAsRead,
		loadMore,
	} = useReleaseNotes(app);

	const handleSelectArticle = (slug: string) => {
		setViewingSlug(slug);
		openArticle(slug);
	};

	const handleBack = () => {
		setViewingSlug(null);
	};

	return (
		<div className="h-full py-1 pr-1">
			<div className="relative bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full flex flex-col">
				<Tabs.Root
					value={activeTab}
					onValueChange={(v) => setActiveTab(v as HelpTab)}
					className="flex flex-col h-full"
				>
					<div className="relative z-50 flex items-center justify-between border-b border-gray-100">
						<Tabs.List className="flex" aria-label="Hilfe Navigation">
							<Tabs.Trigger
								value="hilfe"
								className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 border-b-2 border-transparent transition-colors data-[state=active]:text-gray-900 data-[state=active]:border-gray-900 hover:text-gray-700"
								data-testid="help-tab-hilfe"
							>
								<HelpCircle className="h-3.5 w-3.5" />
								Hilfe
							</Tabs.Trigger>
							<Tabs.Trigger
								value="neuigkeiten"
								className="relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 border-b-2 border-transparent transition-colors data-[state=active]:text-gray-900 data-[state=active]:border-gray-900 hover:text-gray-700"
								data-testid="help-tab-neuigkeiten"
							>
								<Newspaper className="h-3.5 w-3.5" />
								Neuigkeiten
								{unreadCount > 0 && (
									<span className="ml-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-red-500 rounded-full">
										{unreadCount}
									</span>
								)}
							</Tabs.Trigger>
							<Tabs.Trigger
								value="assistent"
								className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 border-b-2 border-transparent transition-colors data-[state=active]:text-gray-900 data-[state=active]:border-gray-900 hover:text-gray-700"
								data-testid="help-tab-assistent"
							>
								<Sparkles className="h-3.5 w-3.5" />
								Assistent
							</Tabs.Trigger>
						</Tabs.List>
						<button
							onClick={closeHelp}
							className="p-1 hover:bg-gray-100 rounded-md transition-colors mr-2"
							aria-label="Hilfe schliessen"
							data-testid="close-help-button"
						>
							<X className="h-5 w-5 text-gray-500" />
						</button>
					</div>

					<Tabs.Content
						value="hilfe"
						forceMount
						className="flex-1 overflow-hidden data-[state=inactive]:hidden"
					>
						<div className="h-full overflow-y-auto p-4">
							{viewingSlug ? (
								<HelpArticleView
									slug={viewingSlug}
									onBack={handleBack}
								/>
							) : (
								<HelpArticleList
									app={app}
									currentRoute={currentRoute}
									onSelectArticle={handleSelectArticle}
								/>
							)}
						</div>
					</Tabs.Content>

					<Tabs.Content
						value="neuigkeiten"
						forceMount
						className="flex-1 overflow-hidden data-[state=inactive]:hidden"
					>
						<ReleaseNotesTab
							releases={releases}
							readIds={readIds}
							loading={loading}
							hasMore={hasMore}
							onMarkAsRead={markAsRead}
							onMarkAllAsRead={markAllAsRead}
							onLoadMore={loadMore}
						/>
					</Tabs.Content>

					<Tabs.Content
						value="assistent"
						forceMount
						className="flex-1 overflow-hidden data-[state=inactive]:hidden"
					>
						<AiChatTab app={app} currentRoute={currentRoute} />
					</Tabs.Content>
				</Tabs.Root>
			</div>
		</div>
	);
};
